import { Body, Heading } from '@assembly-js/design-system';
import clsx from 'clsx';
import parse, {
  DOMNode,
  domToReact,
  Element as HTMLReactParserElement,
  HTMLReactParserOptions,
} from 'html-react-parser';
import React from 'react';

function cssToJsx(cssString: unknown) {
  if (typeof cssString !== 'string') return {};
  const jsxStyle: Record<string, string | number> = {};

  // Remove comments and extra whitespace
  const cleanString = cssString
    .replace(/\/\*[\s\S]*?\*\//g, '') // remove comments
    .trim();

  // Split into individual rules
  const rules = cleanString.split(';').filter((rule) => rule.trim());

  rules.forEach((rule) => {
    const [property, value] = rule.split(':').map((str) => str.trim());

    if (property && value) {
      // Convert kebab-case to camelCase
      const camelCaseProperty = property.replace(/-([a-z])/g, (_, char) =>
        char.toUpperCase(),
      );

      // Optional: convert numeric values to numbers (if unitless)
      jsxStyle[camelCaseProperty] = value;
    }
  });

  return jsxStyle;
}

interface HtmlRendererProps {
  html: string;
  renderOptions?: HTMLReactParserOptions; // Optional custom render options
}

const defaultRenderOptions: HTMLReactParserOptions = {
  replace: (node) => {
    if (
      node instanceof HTMLReactParserElement &&
      node.type === 'tag' &&
      node.attribs
    ) {
      const { name, attribs } = node;
      const children = node.children as DOMNode[];

      switch (name) {
        case 'h3':
          return (
            <Heading size="xl" className="mt-5">
              {domToReact(children, defaultRenderOptions)}
            </Heading>
          );
        case 'p':
          return (
            <Body size="lg" tag="p" className="mt-3 mb-0">
              {domToReact(children, defaultRenderOptions)}
            </Body>
          );
        case 'li':
          return (
            <li {...attribs} className="text-lg text-black-heading mt-1">
              {domToReact(children, defaultRenderOptions)}
            </li>
          );
        case 'a':
          return (
            <a
              {...attribs}
              target="_blank"
              className="text-lg text-black-heading underline"
            >
              {domToReact(children, defaultRenderOptions)}
            </a>
          );

        case 'img':
          const { style, ...rest } = attribs;
          const styleObject = cssToJsx(style);
          return (
            <img
              {...rest}
              style={styleObject}
              className="w-full h-auto mt-3 mx-0 border border-solid rounded-4 border-non-hover-border"
            />
          );

        case 'figure':
          return (
            <figure {...attribs} className="w-full h-auto mt-3 mx-0">
              {domToReact(children, defaultRenderOptions)}
            </figure>
          );
        case 'ol':
          return (
            <ol {...attribs} className="ml-4 mt-3">
              {domToReact(children, defaultRenderOptions)}
            </ol>
          );
        case 'ul':
          return (
            <ul {...attribs} className="ml-4 mt-3">
              {domToReact(children, defaultRenderOptions)}
            </ul>
          );
        case 'video':
          return (
            <video
              controls
              className="w-full h-full rounded-4 border border-solid border-non-hover-border"
            >
              <source src={attribs.src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          );

        default:
          return node;
      }
    } else {
      return null;
    }
  },
};

export const HtmlRenderer: React.FC<HtmlRendererProps> = ({
  html,
  renderOptions,
}) => {
  const mergedRenderOptions: HTMLReactParserOptions = {
    ...defaultRenderOptions,
    ...renderOptions,
  };

  return (
    // The following class names target and hide default video control elements
    // that are auto-injected by the HTML fetched from the latest release API.
    // This ensures a cleaner UI by removing redundant controls.
    <div
      className={clsx(
        '[&_.kg-video-player-container]:hidden [&_.kg-video-overlay]:hidden',
        // Style heading of elements for base and sm screen sizes, font weight, and padding
        '[&_h2]:text-base sm:[&_h2]:text-xl [&_h2]:font-medium [&_h2]:pt-4',
        '[&_h1]:leading-[28px] [&_h1]:mt-4',
        // Remove top margin from images inside figure
        '[&_figure_img]:mt-0',
      )}
    >
      {parse(html, mergedRenderOptions)}
    </div>
  );
};
